import React, { useState, useEffect } from 'react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';
import { useAdminToast } from '../../../../hooks/useAdminToast';
import { ModalWrapper, FormInput } from './ModalComponents';
import { Wallet, ChevronDown, ExternalLink } from 'lucide-react';

const CreateDepositModal = ({ clientId, clientWallets = [], onClose, onSuccess }) => {
  const { toast } = useAdminToast();
  const [form, setForm] = useState({ amount: '', currency: 'USDT', tx_hash: '', notes: '', auto_approve: true });
  const [loading, setLoading] = useState(false);
  const [depositMode, setDepositMode] = useState('manual'); // 'manual' | 'wallet'
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [showWalletDropdown, setShowWalletDropdown] = useState(false);

  // Format wallet address
  const formatAddress = (addr) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';

  const handleSubmit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) return;
    setLoading(true);
    try {
      const depositData = { 
        user_id: clientId, 
        amount: parseFloat(form.amount), 
        ...form,
        source_wallet: selectedWallet?.address || null,
      };
      await adminAPI.createDeposit(depositData);
      
      if (depositMode === 'wallet' && selectedWallet) {
        toast.success(`Создан запрос на депозит с кошелька ${formatAddress(selectedWallet.address)}`);
      } else {
        toast.success('Депозит создан успешно');
      }
      onSuccess();
    } catch (err) {
      toast.apiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper title="Создать депозит" onClose={onClose}>
      {/* Deposit Mode Selection */}
      {clientWallets && clientWallets.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '8px' }}>
            Способ депозита
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <button
              onClick={() => { setDepositMode('manual'); setSelectedWallet(null); }}
              style={{
                padding: '10px',
                backgroundColor: depositMode === 'manual' ? `${COLORS.primary}20` : COLORS.card,
                border: depositMode === 'manual' ? `1px solid ${COLORS.primary}` : `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: depositMode === 'manual' ? COLORS.primary : COLORS.text,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Ручной ввод
            </button>
            <button
              onClick={() => setDepositMode('wallet')}
              style={{
                padding: '10px',
                backgroundColor: depositMode === 'wallet' ? `${COLORS.success}20` : COLORS.card,
                border: depositMode === 'wallet' ? `1px solid ${COLORS.success}` : `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: depositMode === 'wallet' ? COLORS.success : COLORS.text,
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <Wallet size={14} />
              С кошелька
            </button>
          </div>
        </div>
      )}

      {/* Wallet Selection (if wallet mode) */}
      {depositMode === 'wallet' && clientWallets && clientWallets.length > 0 && (
        <div style={{ marginBottom: '16px', position: 'relative' }}>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '8px' }}>
            Кошелёк клиента
          </label>
          <button
            onClick={() => setShowWalletDropdown(!showWalletDropdown)}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: COLORS.background,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.text,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {selectedWallet ? (
              <span style={{ fontFamily: 'monospace' }}>
                {formatAddress(selectedWallet.address)} ({selectedWallet.wallet_type || selectedWallet.chain})
              </span>
            ) : (
              <span style={{ color: COLORS.textSecondary }}>Выберите кошелёк...</span>
            )}
            <ChevronDown size={16} />
          </button>
          
          {showWalletDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              marginTop: '4px',
              zIndex: 10,
              maxHeight: '200px',
              overflowY: 'auto',
            }}>
              {clientWallets.map((wallet, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setSelectedWallet(wallet);
                    setShowWalletDropdown(false);
                  }}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    borderBottom: idx < clientWallets.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                    backgroundColor: selectedWallet?.address === wallet.address ? `${COLORS.primary}10` : 'transparent',
                  }}
                >
                  <div style={{ fontFamily: 'monospace', fontSize: '13px', color: COLORS.text }}>
                    {formatAddress(wallet.address)}
                  </div>
                  <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>
                    {wallet.wallet_type || wallet.chain || 'ETH'}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {selectedWallet && (
            <div style={{ 
              marginTop: '8px', 
              padding: '10px', 
              backgroundColor: `${COLORS.success}10`, 
              borderRadius: '6px',
              fontSize: '12px',
              color: COLORS.textSecondary 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <ExternalLink size={12} />
                <span>При создании депозита клиенту будет отправлен запрос на перевод</span>
              </div>
              <div style={{ color: COLORS.text, fontFamily: 'monospace' }}>
                {selectedWallet.address}
              </div>
            </div>
          )}
        </div>
      )}

      <FormInput label="Сумма ($)" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} type="number" />
      
      {depositMode === 'manual' && (
        <FormInput label="TX Hash (опционально)" value={form.tx_hash} onChange={(v) => setForm({ ...form, tx_hash: v })} />
      )}
      
      <FormInput label="Комментарий" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
      
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: COLORS.text, fontSize: '14px', marginTop: '12px' }}>
        <input 
          type="checkbox" 
          checked={form.auto_approve} 
          onChange={(e) => setForm({ ...form, auto_approve: e.target.checked })} 
          style={{ width: '18px', height: '18px', accentColor: COLORS.success }} 
        />
        Автоматически зачислить на баланс
      </label>
      
      <button 
        onClick={handleSubmit} 
        disabled={loading || !form.amount || (depositMode === 'wallet' && !selectedWallet)} 
        style={{ 
          width: '100%', 
          marginTop: '20px', 
          padding: '14px', 
          backgroundColor: (loading || !form.amount || (depositMode === 'wallet' && !selectedWallet)) ? COLORS.border : COLORS.success, 
          border: 'none', 
          borderRadius: '8px', 
          color: '#fff', 
          fontSize: '15px', 
          fontWeight: '600', 
          cursor: (loading || !form.amount || (depositMode === 'wallet' && !selectedWallet)) ? 'not-allowed' : 'pointer' 
        }}
      >
        {loading ? 'Создание...' : depositMode === 'wallet' ? 'Запросить депозит с кошелька' : 'Создать депозит'}
      </button>
    </ModalWrapper>
  );
};

export default CreateDepositModal;
